interface PerformanceMetric {
  endpoint: string;
  responseTimeMs: number;
  timestamp: Date;
  statusCode?: number;
}

class PerformanceMetricsStore {
  private metrics: PerformanceMetric[] = [];
  private listeners: Set<() => void> = new Set();
  private readonly maxMetrics = 100;

  recordMetric(
    endpoint: string,
    responseTimeMs: number | string | undefined,
    statusCode?: number
  ): void {
    if (!responseTimeMs) return;

    const time =
      typeof responseTimeMs === 'string'
        ? parseInt(responseTimeMs, 10)
        : responseTimeMs;

    if (isNaN(time)) return;

    this.metrics.push({
      endpoint,
      responseTimeMs: time,
      timestamp: new Date(),
      statusCode,
    });

    if (this.metrics.length > this.maxMetrics) {
      this.metrics = this.metrics.slice(-this.maxMetrics);
    }

    this.notifyListeners();
  }

  getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  getStats() {
    if (this.metrics.length === 0) {
      return {
        averageResponseTime: 0,
        slowestEndpoint: null,
        fastestEndpoint: null,
        totalRequests: 0,
        slowRequests: 0,
      };
    }

    const totalTime = this.metrics.reduce(
      (sum, m) => sum + m.responseTimeMs,
      0
    );
    const slowRequests = this.metrics.filter(
      (m) => m.responseTimeMs > 500
    ).length;
    const sorted = [...this.metrics].sort(
      (a, b) => b.responseTimeMs - a.responseTimeMs
    );

    return {
      averageResponseTime: Math.round(totalTime / this.metrics.length),
      slowestEndpoint: sorted[0],
      fastestEndpoint: sorted[sorted.length - 1],
      totalRequests: this.metrics.length,
      slowRequests,
    };
  }

  getMetricsForEndpoint(endpoint: string): PerformanceMetric[] {
    return this.metrics.filter((m) => m.endpoint === endpoint);
  }

  clear(): void {
    this.metrics = [];
    this.notifyListeners();
  }

  subscribe(callback: () => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private notifyListeners(): void {
    this.listeners.forEach((callback) => callback());
  }
}

export const performanceStore = new PerformanceMetricsStore();
